import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import * as couponService from '../../services/adminCoupon.service';
import * as affiliateService from '../../services/adminAffiliate.service';

/**
 * Coupons & Trials — Jenn, 2026-09-10: "coupons should be chosen and
 * administered from our Team365 back end definitely." Two real mechanisms
 * under one page: plain discounts (Stripe's own coupon/promo-code system —
 * this page is a thin window onto Stripe, not a second copy of it) and
 * trial codes ($1/10-day style, or a genuinely free trial — see
 * checkout.controller.js for how these apply at checkout).
 */
export default function AdminCoupons() {
  const [discounts, setDiscounts] = useState(null);
  const [trials, setTrials] = useState(null);
  const [partners, setPartners] = useState([]);
  const [error, setError] = useState(null);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [showTrialForm, setShowTrialForm] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      // Coupons are for any admin (Trish, Patti); the affiliate list only fills
      // the trial form's optional "link to affiliate" picker and is
      // affiliate-manager-only, so its 403 mustn't blank the whole page.
      const [d, t, p] = await Promise.all([
        couponService.listDiscountCodes(),
        couponService.listTrialCodes(),
        affiliateService.listPartners().catch(() => []),
      ]);
      setDiscounts(d);
      setTrials(t);
      setPartners(p.filter(pp => pp.partner_type === 'affiliate'));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load coupons.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDeactivateDiscount = async (id) => {
    if (!window.confirm('Deactivate this code? It stops working immediately.')) return;
    try { await couponService.deactivateDiscountCode(id); load(); }
    catch (err) { setError(err.response?.data?.error || 'Could not deactivate this code.'); }
  };

  const handleDeactivateTrial = async (id) => {
    if (!window.confirm('Deactivate this code? It stops working immediately.')) return;
    try { await couponService.deactivateTrialCode(id); load(); }
    catch (err) { setError(err.response?.data?.error || 'Could not deactivate this code.'); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <AdminNav />
        <h1 className="font-serif text-2xl font-bold text-reset-brown mt-2 mb-1">Coupons & Trials</h1>
        <p className="text-sm text-gray-500 mb-6">Every discount and trial code that works at checkout — created and turned off from here, not Stripe's dashboard.</p>

        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}

        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-reset-brown">Discount codes</h2>
            <button onClick={() => setShowDiscountForm(v => !v)} className="text-xs font-medium text-reset-terracotta hover:underline">
              {showDiscountForm ? 'Cancel' : '+ New discount code'}
            </button>
          </div>
          {showDiscountForm && <DiscountForm onSaved={() => { setShowDiscountForm(false); load(); }} onError={setError} />}

          {discounts == null ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : discounts.length === 0 ? (
            <p className="text-sm text-gray-400">No discount codes yet.</p>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
              {discounts.map(c => (
                <div key={c.id} className="px-5 py-3 flex items-center justify-between text-sm">
                  <div>
                    <span className="font-mono font-medium text-reset-brown">{c.code}</span>
                    <span className="text-gray-400 ml-2">
                      {c.percent_off ? `${c.percent_off}% off` : `$${(c.amount_off / 100).toFixed(2)} off`}
                      {' · '}{c.duration}{c.duration === 'repeating' ? ` (${c.duration_in_months}mo)` : ''}
                    </span>
                    {!c.active && <span className="text-amber-600 ml-2">· inactive</span>}
                  </div>
                  <div className="flex items-center gap-3 text-gray-400">
                    <span>{c.times_redeemed}{c.max_redemptions ? `/${c.max_redemptions}` : ''} used</span>
                    {c.active && <button onClick={() => handleDeactivateDiscount(c.id)} className="text-red-500 hover:underline">Deactivate</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-reset-brown">Trial codes</h2>
            <button onClick={() => setShowTrialForm(v => !v)} className="text-xs font-medium text-reset-terracotta hover:underline">
              {showTrialForm ? 'Cancel' : '+ New trial code'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mb-3">A real trial period (e.g. 10 days), with an optional small fee (e.g. $1) charged up front — converts to full price automatically the moment the trial ends.</p>
          {showTrialForm && <TrialForm partners={partners} onSaved={() => { setShowTrialForm(false); load(); }} onError={setError} />}

          {trials == null ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : trials.length === 0 ? (
            <p className="text-sm text-gray-400">No trial codes yet.</p>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
              {trials.map(c => (
                <div key={c.id} className="px-5 py-3 flex items-center justify-between text-sm">
                  <div>
                    <span className="font-mono font-medium text-reset-brown">{c.code}</span>
                    <span className="text-gray-400 ml-2">
                      {c.label} · {c.trial_days} days{c.trial_fee_cents > 0 ? ` for $${(c.trial_fee_cents / 100).toFixed(2)}` : ' free'}
                    </span>
                    {!c.is_active && <span className="text-amber-600 ml-2">· inactive</span>}
                  </div>
                  <div className="flex items-center gap-3 text-gray-400">
                    <span>{c.times_redeemed}{c.max_redemptions ? `/${c.max_redemptions}` : ''} used</span>
                    {c.is_active && <button onClick={() => handleDeactivateTrial(c.id)} className="text-red-500 hover:underline">Deactivate</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}

function DiscountForm({ onSaved, onError }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState('percent'); // percent | amount
  const [value, setValue] = useState('');
  const [duration, setDuration] = useState('once');
  const [durationInMonths, setDurationInMonths] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!code.trim() || !value) return;
    setSaving(true);
    try {
      await couponService.createDiscountCode({
        code: code.trim(), name: name.trim() || undefined,
        percentOff: kind === 'percent' ? Number(value) : undefined,
        amountOffCents: kind === 'amount' ? Math.round(Number(value) * 100) : undefined,
        duration, durationInMonths: duration === 'repeating' ? Number(durationInMonths) : undefined,
        maxRedemptions: maxRedemptions ? Number(maxRedemptions) : undefined,
      });
      onSaved();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not create this code.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-100 rounded-xl p-4 mb-4 space-y-2 bg-white">
      <Field label="Code — what the person types at checkout">
        <input value={code} onChange={e => setCode(e.target.value)} placeholder="e.g. WELCOME10"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      </Field>
      <Field label="Name — who it's for or why (only Team365 sees this, up to 40 characters)">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Optional" maxLength={40}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        <p className="text-xs text-gray-400 mt-1">{name.length}/40</p>
      </Field>
      <Field label="How much off">
      <div className="flex gap-2">
        <select value={kind} onChange={e => setKind(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="percent">% off</option>
          <option value="amount">$ off</option>
        </select>
        <input type="number" step="0.01" value={value} onChange={e => setValue(e.target.value)}
          placeholder={kind === 'percent' ? 'e.g. 10' : 'e.g. 5.00'}
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      </div>
      </Field>
      <Field label="Which payments it takes money off">
      <div className="flex gap-2">
        <select value={duration} onChange={e => setDuration(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="once">Once (first payment only)</option>
          <option value="repeating">Repeating (N months)</option>
          <option value="forever">Forever</option>
        </select>
        {duration === 'repeating' && (
          <input type="number" value={durationInMonths} onChange={e => setDurationInMonths(e.target.value)}
            placeholder="Months" className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        )}
      </div>
      </Field>
      <Field label="How many people can use it (leave blank for no limit)">
        <input type="number" value={maxRedemptions} onChange={e => setMaxRedemptions(e.target.value)}
          placeholder="Blank = unlimited"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      </Field>
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-1.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
          {saving ? 'Creating…' : 'Create'}
        </button>
      </div>
    </div>
  );
}

// Jenn, 2026-10-09: "label these fields so I know what I am entering" —
// placeholders vanish once a box has a value, so every box gets a visible label.
function Field({ label, className = '', children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-medium text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function TrialForm({ partners, onSaved, onError }) {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [trialDays, setTrialDays] = useState('10');
  const [feeDollars, setFeeDollars] = useState('1');
  const [tier, setTier] = useState('regular');
  const [affiliatePartnerId, setAffiliatePartnerId] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!code.trim() || !label.trim() || !trialDays) return;
    setSaving(true);
    try {
      await couponService.createTrialCode({
        code: code.trim(), label: label.trim(), trialDays: Number(trialDays),
        trialFeeCents: Math.round(Number(feeDollars || 0) * 100), tier,
        affiliatePartnerId: affiliatePartnerId || undefined,
        maxRedemptions: maxRedemptions ? Number(maxRedemptions) : undefined,
      });
      onSaved();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not create this trial code.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-100 rounded-xl p-4 mb-4 space-y-2 bg-white">
      <Field label="Code — what the person types at checkout">
        <input value={code} onChange={e => setCode(e.target.value)} placeholder="e.g. PATTI1DOLLAR"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      </Field>
      <Field label="Label — who it's for or why (only Team365 sees this)">
        <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Patti's $1/10-day affiliate code"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      </Field>
      <div className="flex gap-2">
        <Field label="Free days" className="flex-1">
          <input type="number" value={trialDays} onChange={e => setTrialDays(e.target.value)} placeholder="e.g. 10"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </Field>
        <Field label="Charged today, $ (0 = completely free)" className="flex-1">
          <input type="number" step="0.01" value={feeDollars} onChange={e => setFeeDollars(e.target.value)} placeholder="0"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </Field>
      </div>
      <Field label="Membership they move onto when the free days end (charged automatically)">
        <select value={tier} onChange={e => setTier(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="regular">Regular membership</option>
          <option value="founder">Founder membership</option>
        </select>
      </Field>
      <Field label="Affiliate who gets credit (optional)">
        <select value={affiliatePartnerId} onChange={e => setAffiliatePartnerId(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="">Not tied to a specific affiliate</option>
          {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <Field label="How many people can use it (leave blank for no limit)">
        <input type="number" value={maxRedemptions} onChange={e => setMaxRedemptions(e.target.value)}
          placeholder="Blank = unlimited"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      </Field>
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-1.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
          {saving ? 'Creating…' : 'Create'}
        </button>
      </div>
    </div>
  );
}
